import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { categoriesApi } from '../api/categories';
import { toErrorMessage } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useStores } from '../auth/StoreContext';
import { ActiveBadge, Card, PageHead } from '../components/ui';
import { StoreSelector } from '../components/StoreSelector';
import { Empty, ErrorState, Loading } from '../components/DataStates';
import { Modal } from '../components/Modal';
import type { Category, CategoryCreateRequest } from '../types';

export function CategoriesPage() {
  const qc = useQueryClient();
  const { isSuperAdmin } = useAuth();
  const { selectedStoreId } = useStores();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<Category | null>(null);

  // Store admins are scoped to their own store; super admins can pick one.
  const storeId = selectedStoreId;

  const q = useQuery({
    queryKey: ['categories', storeId],
    queryFn: () => categoriesApi.list({ storeId, activeOnly: false }),
  });

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['categories'] });

  const deactivate = useMutation({
    mutationFn: (id: number) => categoriesApi.deactivate(id),
    onSuccess: invalidate,
  });

  return (
    <>
      <PageHead
        title="Categories"
        subtitle="Organise the menu into sections."
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            + New Category
          </button>
        }
      />

      {isSuperAdmin && (
        <div className="toolbar">
          <StoreSelector allowAll label="Store" />
          <span className="hint">
            Leave “All stores” to manage the shared/global menu.
          </span>
        </div>
      )}

      <Card>
        {q.isLoading ? (
          <Loading />
        ) : q.isError ? (
          <ErrorState message={toErrorMessage(q.error)} onRetry={q.refetch} />
        ) : !q.data || q.data.length === 0 ? (
          <Empty icon="🗂️" title="No categories yet" />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 90 }}>Order</th>
                  <th>Name</th>
                  <th>Scope</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {[...q.data]
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((c) => (
                    <tr key={c.id}>
                      <td className="mono">{c.displayOrder}</td>
                      <td>
                        <strong>{c.name}</strong>
                      </td>
                      <td>
                        {c.storeId == null ? (
                          <span className="badge badge-blue">Global</span>
                        ) : (
                          <span className="badge badge-gray">Store</span>
                        )}
                      </td>
                      <td>
                        <ActiveBadge active={c.active} />
                      </td>
                      <td className="text-right">
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => setEditing(c)}
                        >
                          Edit
                        </button>{' '}
                        {c.active && (
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() => {
                              if (
                                window.confirm(
                                  `Deactivate category “${c.name}”?`,
                                )
                              )
                                deactivate.mutate(c.id);
                            }}
                          >
                            Deactivate
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {creating && (
        <CategoryFormModal
          title="New Category"
          initial={{
            name: '',
            displayOrder: (q.data?.length ?? 0) + 1,
            storeId,
            active: true,
          }}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            invalidate();
          }}
          submit={(d) => categoriesApi.create(d)}
        />
      )}

      {editing && (
        <CategoryFormModal
          title={`Edit ${editing.name}`}
          initial={{
            name: editing.name,
            displayOrder: editing.displayOrder,
            storeId: editing.storeId,
            active: editing.active,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            invalidate();
          }}
          submit={(d) => categoriesApi.update(editing.id, d)}
        />
      )}
    </>
  );
}

function CategoryFormModal({
  title,
  initial,
  onClose,
  onSaved,
  submit,
}: {
  title: string;
  initial: CategoryCreateRequest;
  onClose: () => void;
  onSaved: () => void;
  submit: (d: CategoryCreateRequest) => Promise<Category>;
}) {
  const [draft, setDraft] = useState<CategoryCreateRequest>(initial);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => submit(draft),
    onSuccess: onSaved,
    onError: (err) => setError(toErrorMessage(err)),
  });

  const set = (patch: Partial<CategoryCreateRequest>) =>
    setDraft((d) => ({ ...d, ...patch }));

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mutation.mutate();
  }

  return (
    <Modal
      open
      title={title}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={mutation.isPending || !draft.name}
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      <form onSubmit={onSubmit}>
        {error && <div className="form-error">{error}</div>}
        <div className="field">
          <label>Name *</label>
          <input
            value={draft.name}
            onChange={(e) => set({ name: e.target.value })}
            autoFocus
            required
          />
        </div>
        <div className="field">
          <label>Display Order</label>
          <input
            type="number"
            value={draft.displayOrder ?? 0}
            onChange={(e) => set({ displayOrder: Number(e.target.value) })}
          />
        </div>
        <div className="checkbox-row">
          <input
            id="catActive"
            type="checkbox"
            checked={draft.active ?? true}
            onChange={(e) => set({ active: e.target.checked })}
          />
          <label htmlFor="catActive">Active</label>
        </div>
      </form>
    </Modal>
  );
}
